import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WorkspaceTitle from './WorkspaceTitle';
import styles from './index.module.scss';
import { Button } from 'src/common';
import BidsBoard from './BidsBoard';
import { useWorkspace, useWorkspaceViewState } from 'src/models/workspace/hooks';
import { trackSwitchWorkspaceViewMode } from 'src/segment/events';
import BidsSearch from './BidsSearch';
import BidsTable from './BidsTable';
import BidsDndLayer from './BidsTable/DndLayer';
import { PageView } from 'src/models/users/Preferences/types';
import ViewOptionsDropDown from 'src/shared/Tenders/ViewOptionsDropDown';
import BidsFilter from './BidsFilter';
import useBidsSearchArgs from 'src/reactiveVars/BidsSearchArgsVar';
import WinHitRateLabel from './WinHitRateLabel';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CreateManualBidModal from 'src/shared/Workspace/CreateManualBidModal';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import BidsSidebars from './BidsSidebars';
import BidsSorting from './BidsSorting';

export const BidsPage: FC = () => {
  const { t } = useTranslation();
  const [{ rejected: isRejectedTab }] = useBidsSearchArgs();
  const isManualBidsFeature = useFeatureFlag(FeatureFlag.ManualBids);

  const { data: ws, loading } = useWorkspace();

  const [createManualBidVisible, setCreateManualBidVisible] = useState(false);
  const onOpen = useCallback(() => {
    setCreateManualBidVisible(true);
  }, []);
  const onClose = useCallback(() => {
    setCreateManualBidVisible(false);
  }, []);

  const [currentView, setWsView] = useWorkspaceViewState();
  const toggleView = useCallback(
    (view: PageView) => {
      setWsView(view);
      ws && trackSwitchWorkspaceViewMode({ id: ws.id }, view);
    },
    [setWsView, ws]
  );

  const isKanban = useMemo(() => currentView === PageView.board, [currentView]);

  if (!ws) {
    return null;
  }

  return (
    <>
      <BidsDndLayer />
      <div className={styles.content}>
        {!ws.isActive && <div className={styles.archived}>{t('BidSpaces.bidSpaceIsArchived')}</div>}
        <WorkspaceTitle pending={loading} workspace={ws} />
        <div className={styles.actionBar}>
          <ul className={styles.viewActions}>
            {!isRejectedTab && (
              <>
                {isManualBidsFeature && (
                  <li>
                    <Button
                      type={'text'}
                      onClick={onOpen}
                      icon={<FontAwesomeIcon icon={faPlus} className={styles.itemIcon} />}
                    >
                      {t('BidSpaces.WorkspaceCreateManualBid.createBid')}
                    </Button>
                  </li>
                )}
                <li className={styles.viewAction}>
                  <WinHitRateLabel />
                </li>
              </>
            )}
            {isKanban && (
              <li className={styles.viewAction}>
                <BidsSorting wsId={ws.id} />
              </li>
            )}
            <li className={styles.viewAction}>
              <ViewOptionsDropDown
                extra={<BidsFilter />}
                onToggleView={toggleView}
                currentView={currentView}
                tableViewText={t('Common.tableView')}
                cardViewText={t('Common.boardView')}
              />
            </li>
          </ul>
          <BidsSearch />
        </div>
        {isKanban ? <BidsBoard /> : <BidsTable />}
        {createManualBidVisible && <CreateManualBidModal onClose={onClose} />}
      </div>
      <BidsSidebars />
    </>
  );
};

export default BidsPage;
